// Отметки ленты на ЭТОМ устройстве (T-047): галочка уведомлений Windows, «прочитано»
// и «уже показано уведомлением» — по точкам.
//
// Устройства, а не человека (решение владельца 2026-09-29): общий планшет у полки,
// и «прочитано» на человека владелец отклонил. Ключи со временем жизни `device`
// переживают пересменку — урок B-039, где ключ `person` стирался при выходе.
//
// Копия в памяти — чтобы счётчик на колокольчике считался синхронно на рендере.
// Запись в память — сразу, на диск — вдогонку: второй эффект StrictMode видит уже
// сдвинутую отметку и не показывает уведомление дважды.

import { Preferences } from '@capacitor/preferences';

import { PREFERENCE_KEYS } from '@/shared/storage';

import { instant } from './mergeFeed';

type Marks = Readonly<Record<string, string>>;

let notify = false;
let nudgeDismissed = false;
let seen: Marks = {};
let notified: Marks = {};
let version = 0;
const listeners = new Set<() => void>();

function changed(): void {
  version += 1;
  for (const listener of listeners) listener();
}

function parse(value: string | null): Marks {
  if (!value) return {};
  try {
    const parsed: unknown = JSON.parse(value);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, string] => typeof entry[1] === 'string',
      ),
    );
  } catch {
    // Испорченная запись — не повод ронять колокольчик: отметка просто начнётся заново.
    return {};
  }
}

/** Прочитать отметки с диска — один раз на старте, до первого рендера (`main.tsx`). */
export async function restoreDeviceMarks(): Promise<void> {
  const [on, read, shown, dismissed] = await Promise.all([
    Preferences.get({ key: PREFERENCE_KEYS.alertsNotify }),
    Preferences.get({ key: PREFERENCE_KEYS.alertsSeen }),
    Preferences.get({ key: PREFERENCE_KEYS.alertsNotified }),
    Preferences.get({ key: PREFERENCE_KEYS.alertsNudgeDismissed }),
  ]);
  notify = on.value === '1';
  nudgeDismissed = dismissed.value === '1';
  seen = parse(read.value);
  notified = parse(shown.value);
  changed();
}

export function subscribeDeviceMarks(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Снимок для `useSyncExternalStore`: число меняется при любой смене отметок. */
export function deviceMarksVersion(): number {
  return version;
}

export function notifyEnabled(): boolean {
  return notify;
}

/**
 * Галочка «Уведомления Windows на этом устройстве». Включение сбрасывает отметки «показано»:
 * первый прогон после включения ставит их на самое свежее и историю пачкой не вываливает.
 */
export async function setNotifyEnabled(on: boolean): Promise<void> {
  notify = on;
  if (on) notified = {};
  changed();
  if (on) {
    await Preferences.set({ key: PREFERENCE_KEYS.alertsNotify, value: '1' });
    await Preferences.remove({ key: PREFERENCE_KEYS.alertsNotified });
  } else {
    await Preferences.remove({ key: PREFERENCE_KEYS.alertsNotify });
  }
}

/** Скрыли ли плашку «Включить уведомления Windows» на этом устройстве (№10). */
export function nudgeHidden(): boolean {
  return nudgeDismissed;
}

export async function hideNudge(): Promise<void> {
  nudgeDismissed = true;
  changed();
  await Preferences.set({ key: PREFERENCE_KEYS.alertsNudgeDismissed, value: '1' });
}

export function seenMark(location: string): string | null {
  return seen[location] ?? null;
}

export function notifiedMark(location: string): string | null {
  return notified[location] ?? null;
}

/** Отметка только вперёд: поздний ответ старого запроса не откатывает «прочитано». */
function forward(marks: Marks, location: string, at: string): Marks | null {
  const current = marks[location];
  if (current !== undefined && instant(current) >= instant(at)) return null;
  return { ...marks, [location]: at };
}

export function markSeen(location: string, at: string): void {
  const next = forward(seen, location, at);
  if (next === null) return;
  seen = next;
  changed();
  void Preferences.set({ key: PREFERENCE_KEYS.alertsSeen, value: JSON.stringify(next) });
}

export function markNotified(location: string, at: string): void {
  const next = forward(notified, location, at);
  if (next === null) return;
  notified = next;
  changed();
  void Preferences.set({ key: PREFERENCE_KEYS.alertsNotified, value: JSON.stringify(next) });
}

/** Только для тестов. */
export function __resetDeviceMarksForTests(): void {
  notify = false;
  nudgeDismissed = false;
  seen = {};
  notified = {};
  changed();
}
